import './SnapRow.scss'

import cls from 'classnames'
import { memo, useEffect, useRef, type ReactNode } from 'react'

type SnapRowAlign = 'start' | 'center' | 'end'
type SnapRowSnap = 'proximity' | 'mandatory' | 'none'

export type SnapRowItemProps<T> = {
	readonly index: number
	readonly isActive: boolean
	readonly item: T
}

export type SnapRowRenderItem<T> = (props: SnapRowItemProps<T>) => ReactNode
export type SnapRowKeyExtractor<T> = (props: SnapRowItemProps<T>) => string | number

export type SnapRowProps<T> = {
	readonly activeIndex?: number
	readonly align?: SnapRowAlign
	readonly className?: string
	readonly gap?: number
	readonly items: T[]
	readonly itemWidth: number
	readonly keyExtractor?: SnapRowKeyExtractor<T>
	readonly renderItem: SnapRowRenderItem<T>
	readonly snap?: SnapRowSnap
}

const SnapRowInner = <T,>({
	activeIndex,
	align = 'center',
	className,
	gap = 0,
	items,
	itemWidth,
	keyExtractor = defaultKeyExtractor,
	renderItem,
	snap = 'proximity',
}: SnapRowProps<T>) => {
	const itemRefs = useRef<(HTMLDivElement | null)[]>([])

	const snapClasses = SNAP_CLS_MAP[snap]
	const snapAlignClass = snap == 'none' ? '' : SNAP_ALIGN_CLS_MAP[align]

	const setItemRef = (index: number) => (el: HTMLDivElement | null) => {
		itemRefs.current[index] = el
	}

	useEffect(() => {
		if (activeIndex == null) return

		const $activeEl = itemRefs.current[activeIndex]

		if (!$activeEl) return

		$activeEl.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: align,
		})
	}, [activeIndex, align])

	return (
		<div
			className={cls(
				'snaprow-container h-full w-full overflow-x-auto overflow-y-hidden',
				snapClasses,
				className,
			)}
		>
			<div className="flex h-full items-center" style={{ gap }}>
				{items.map((item, index) => {
					const isActive = activeIndex === index
					const snapRowItemProps = { index, item, isActive }
					const key = keyExtractor(snapRowItemProps)

					return (
						<div
							key={key}
							ref={setItemRef(index)}
							className={cls('shrink-0', snapAlignClass)}
							style={{ width: itemWidth }}
						>
							{renderItem(snapRowItemProps)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

export const SnapRow = memo(SnapRowInner) as <T>(
	props: SnapRowProps<T>,
) => ReturnType<typeof SnapRowInner>

const SNAP_ALIGN_CLS_MAP = {
	start: 'snap-start',
	center: 'snap-center',
	end: 'snap-end',
} satisfies { readonly [K in SnapRowAlign]: string }

const SNAP_CLS_MAP = {
	mandatory: 'snap-x snap-mandatory',
	proximity: 'snap-x snap-proximity',
	none: '',
} satisfies { readonly [K in SnapRowSnap]: string }

const defaultKeyExtractor = <T,>({ index }: SnapRowItemProps<T>) => index
